import { createUpdateSyncSlice } from '@sonar-software/xuder';
import { defaultSonarObjectsIdMapState } from './state';

import { lowerFirst, rebuildArray } from '@lib';
import { SonarObjectReference, UpdateAllSonarObjectReferencesRequest, UpdateSonarObjectReferenceRequest } from '@types';

const empty: SonarObjectReference = {
	aiGeneratedTracking: undefined,
	context: {
		definition: '',
		notes: ''
	},
	hasFieldSpyMetadata: true,
	ingestedBy: [],
	initiativeIds: [],
	linkedIntegrations: [],
	populatedBy: [],
	relatedCounts: {},
	salesforce: {},
	stakeholders: [],
	tagIds: [],
	users: []
};

const getMerge = <T, K extends keyof T>(entry?: T, existingEntry?: T, optimisticEntry?: T) =>
	<F>(key: K, fallback: F) => (entry?.[ key ] ?? existingEntry?.[ key ] ?? optimisticEntry?.[ key ] ?? fallback) as F;

function rebuildReferenceEntry(
	entry: UpdateSonarObjectReferenceRequest | UpdateAllSonarObjectReferencesRequest,
	existingEntry: SonarObjectReference
): SonarObjectReference {
	const merge = getMerge(entry, existingEntry);
	const mergeSalesforce = getMerge(
		entry.salesforce,
		existingEntry.salesforce
	);

	return {
		...existingEntry,
		aiGeneratedTracking: merge('aiGeneratedTracking', empty.aiGeneratedTracking),
		context: {
			...existingEntry.context,
			...entry.context
		},
		displayName: merge('displayName', empty.displayName),
		hasFieldSpyMetadata: merge('hasFieldSpyMetadata', empty.hasFieldSpyMetadata),
		ingestedBy: merge('ingestedBy', empty.ingestedBy),
		initiativeIds: rebuildArray(existingEntry.initiativeIds, entry.initiatives),
		internalName: merge('internalName', empty.internalName),
		isActive: merge('isActive', empty.isActive),
		isDeleted: merge('isDeleted', empty.isDeleted),
		linkedIntegrations: merge('linkedIntegrations', empty.linkedIntegrations),
		parentDisplayName: merge('parentDisplayName', empty.parentDisplayName),
		parentInternalName: merge('parentInternalName', empty.parentInternalName),
		parentSonarObjectId: merge('parentSonarObjectId', empty.parentSonarObjectId),
		populatedBy: merge('populatedBy', empty.populatedBy),
		rawSalesforceType: merge('rawSalesforceType', empty.rawSalesforceType),
		relatedCounts: {
			...existingEntry.relatedCounts,
			...entry.relatedCounts
		},
		salesforce: {
			...existingEntry.salesforce,
			...entry.salesforce,
			complianceCategory: mergeSalesforce('complianceCategory', empty.salesforce.complianceCategory),
			dataSensitivityLevelId: mergeSalesforce('dataSensitivityLevelId', empty.salesforce.dataSensitivityLevelId),
			description: mergeSalesforce('description', empty.salesforce.description),
			fieldUsageId: mergeSalesforce('fieldUsageId', empty.salesforce.fieldUsageId),
			helpText: mergeSalesforce('helpText', empty.salesforce.helpText),
			isCustom: mergeSalesforce('isCustom', empty.salesforce.isCustom),
			managedPackage: mergeSalesforce('managedPackage', empty.salesforce.managedPackage),
			pageLayouts: mergeSalesforce('pageLayouts', empty.salesforce.pageLayouts),
			profiles: mergeSalesforce('profiles', empty.salesforce.profiles)
		},
		sonarObjectType: lowerFirst(merge('sonarObjectType', empty.sonarObjectType)),
		sourceUrl: merge('sourceUrl', empty.sourceUrl),
		stakeholders: merge('stakeholders', empty.stakeholders),
		tagIds: rebuildArray(existingEntry.tagIds, entry.tags),
		users: merge('users', empty.users),
		versionNumber: merge('versionNumber', empty.versionNumber)
	};
}

export const coreSonarObjectsIdMapSlice = createUpdateSyncSlice({
	name: 'CoreSonarObjectsIdMap',
	state: defaultSonarObjectsIdMapState,
	transform: (action: Array<UpdateSonarObjectReferenceRequest>, state = {}) => {
		if (!action.length) {
			return state;
		}

		for (const entry of action) {
			const key = entry.sonarObjectId;

			state[ key ] = rebuildReferenceEntry(entry, state[ key ] ?? { ...empty });
		}

		return state;
	}
});

export const coreSonarObjectsIdMapAllSlice = createUpdateSyncSlice({
	name: 'CoreSonarObjectsIdMapAll',
	state: defaultSonarObjectsIdMapState,
	transform: (action: UpdateAllSonarObjectReferencesRequest, state = {}) => {
		for (const key in state) {
			state[ key ] = rebuildReferenceEntry(action, state[ key ] ?? { ...empty });
		}

		return state;
	}
});
